import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TOOLTIP_DEFAULT_DELAY } from "@/components/common/TooltipLayer";

interface RichTooltipProps {
	/**
	 * ツールチップの中身。プレーンテキストで足りる場合は title 属性 (TooltipLayer) を使う。
	 * null / undefined を渡すとホバーしても何も出さない。アンカー自体は描き続けるため、
	 * 出し分けたい呼び出し側が要素を差し替えずに済む。
	 */
	content: React.ReactNode;
	/** 既定の max-width (280px) を上書きしたい場合に指定する。 */
	maxWidth?: number;
	side?: "top" | "right" | "bottom" | "left";
	/**
	 * ホバーしてから出すまでの遅延 (ミリ秒)。既定は title 属性の tooltip と同じ。
	 * 遅延の間に状態が変わって案内が消えてしまう箇所だけ 0 を渡す。
	 */
	delayMs?: number;
	/** アンカーになる要素。ホバーで表示する。 */
	children: React.ReactNode;
}

/**
 * ReactNode を中身にできるホバー式ツールチップ。title 属性ベースの TooltipLayer は
 * プレーンテキスト限定のため、リスト等のリッチな整形が要る箇所はこちらを使う。
 * 見た目 (.izanagi-tooltip) と CSS anchor positioning は TooltipLayer と共有する。
 * アンカーがモーダル内の場合は dialog 要素へポータルし、同じ top layer 上に表示する。
 */
export function RichTooltip({
	content,
	maxWidth,
	side = "bottom",
	delayMs = TOOLTIP_DEFAULT_DELAY,
	children,
}: RichTooltipProps) {
	const anchorRef = useRef<HTMLSpanElement>(null);
	const [hovered, setHovered] = useState(false);
	const [shown, setShown] = useState(false);
	const hasContent = content !== null && content !== undefined;
	const visible = shown && hasContent;

	// ホバーしたままでも中身や遅延は入れ替わる (送信ボタンは状態ごとに別の案内を出す)。
	// 中身が変わったら新しい遅延を頭から測り直す。
	useEffect(() => {
		if (!hovered || !hasContent) {
			setShown(false);
			return;
		}
		if (delayMs <= 0) {
			setShown(true);
			return;
		}
		setShown(false);
		const timer = setTimeout(() => setShown(true), delayMs);
		return () => clearTimeout(timer);
	}, [hovered, hasContent, delayMs]);

	// anchor-name はカスタムプロパティ扱いで React の style オブジェクトから設定できないため、
	// TooltipLayer と同様に DOM へ直接設定する。表示中は同時にホバーできる要素が他にない
	// (title ベースの tooltip も含め)、共有名で衝突しない。
	useEffect(() => {
		const el = anchorRef.current;
		if (!el) return;
		if (visible) {
			el.style.setProperty("anchor-name", "--izanagi-tooltip");
			return () => {
				el.style.removeProperty("anchor-name");
			};
		}
	}, [visible]);

	return (
		<span
			ref={anchorRef}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			// アンカーは中身のサイズを保つ。flex 行に置かれたときに縮められると、
			// 包んでいるアイコンやボタンごと潰れてしまう。
			style={{ display: "inline-flex", flexShrink: 0 }}
		>
			{children}
			{visible &&
				anchorRef.current !== null &&
				createPortal(
					<div
						className="izanagi-tooltip"
						data-side={side}
						data-testid="izanagi-tooltip"
						style={maxWidth !== undefined ? { maxWidth } : undefined}
					>
						{content}
					</div>,
					anchorRef.current.closest("dialog") ?? document.body,
				)}
		</span>
	);
}
